import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApiClient } from "../api/ApiClientContext";
import { Viewer } from "../components/Viewer";
import { SaveStatePill } from "../components/SaveStatePill";
import { RefreshButton } from "../components/RefreshButton";
import { EditorList } from "../components/EditorList";
import { ExportMenu } from "../components/ExportMenu";
import { OverlayMenu } from "../components/OverlayMenu";
import { OverlayDropZone } from "../components/OverlayDropZone";
import { useOverlayManager } from "../overlay/useOverlayManager";
import { useOnline } from "../offline/useOnline";
import { SetNavChip } from "../components/SetNavChip";
import { TeamPill } from "../components/TeamPill";
import { ScopeMenu, type ScopeOption } from "../components/ScopeMenu";
import { NextFlightPrompt } from "../components/NextFlightPrompt";
import { loadOverlays, type LoadedOverlay } from "../overlay/kmzOverlay";
import { loadGhostTracks, type GhostTrack } from "../set/ghostTracks";
import { loadCachedOverlays } from "../offline/offlineOverlays";
import { loadCachedGhostTracks } from "../offline/offlineGhosts";
import { EvictionRecoveryModal } from "../components/EvictionRecoveryModal";
import {
  SavestateDialog,
  type QualityOption,
  type SavestateProgress,
} from "../components/SavestateDialog";
import { buildKmz, kmzFilename } from "../export/kmz";
import { parseMasterRungs, offlineSelectableRungs } from "../export/hlsMaster";
import { makeFetchManifest } from "../offline/fetchManifest";
import { downloadFlightForOffline, OfflineQuotaExceededError } from "../offline/download";
import { buildDownloadInputFromDetail, refreshFlight } from "../offline/refreshFlight";
import { fetchTrack } from "../export/track";
import { auditFlight, type AuditResult } from "../offline/audit";
import { AnnotationStore, type SaveState } from "../state/AnnotationStore";
import { AnnotationsApi } from "../api/annotations";
import { OfflineQueue, AuthExpiredError } from "../state/OfflineQueue";
import { useAuth } from "../auth/AuthContext";
import type { Annotations, FlightDetailResponse, PanoView } from "../api/types";
import type { ViewerHandle } from "@viewer";
import { useTourAutoStart } from "../tour/useTourAutoStart";
import { useTourChapterHook } from "../tour/useTourChapterHook";
import { parseOpenPanorama } from "./panoParams";
import { pillState } from "./pillState";
import { buildOfflineDetail } from "./offlineDetail";
import {
  chooseVideoSource,
  resolveCachedFlight,
  type CachedFlightInfo,
} from "./videoSource";
import { nswTileLayers, selectTileLayers, usesEsri } from "../map/basemaps";
import { tileMeter } from "../map/tileMeter";
import { setPlaying } from "../presence/playing";

interface StoreSnapshotState {
  saveState: SaveState;
  annotations: Annotations | null;
}

/** Shared empty ghost list. Resetting state to this exact array (rather than a
 *  fresh `[]`) lets React bail out of the update when there is nothing to
 *  clear, which keeps the Viewer's identity-keyed ghost effect from re-firing. */
const NO_GHOSTS: GhostTrack[] = [];

export function ViewerPage(): JSX.Element {
  const { id: flightId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get("team") ?? undefined;
  const tParamRaw = searchParams.get("t");
  const tParam = tParamRaw !== null && tParamRaw !== "" && Number.isFinite(Number(tParamRaw))
    ? Number(tParamRaw)
    : undefined;
  const openPanorama = useMemo(
    () => parseOpenPanorama(searchParams),
    [searchParams],
  );
  const apiClient = useApiClient();
  const {
    displayName, team: ownTeam, reviewerId: ownReviewerId, token, esriApiKey,
  } = useAuth();
  // The Worker base URL the JWT is scoped to — same value main.tsx uses to
  // construct the ApiClient. The viewer attaches `authToken` only to fetches
  // against this origin.
  const workerOrigin = import.meta.env.VITE_API_URL ?? "http://localhost:8787";
  // Authed GET of a Worker manifest URL as text — used to enumerate HLS rungs
  // for the quality picker and during the offline download.
  const fetchManifest = useMemo(
    () => makeFetchManifest(token, workerOrigin), [token, workerOrigin],
  );
  const annotationsApi = useMemo(() => new AnnotationsApi(apiClient), [apiClient]);

  const [detail, setDetail] = useState<FlightDetailResponse | null>(null);
  const [overlays, setOverlays] = useState<LoadedOverlay[]>([]);
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [overlaysReady, setOverlaysReady] = useState(false);
  // Adding and removing the team's overlays from this page (spec 2026-09-09).
  // Offline the controls are disabled; the cached copies stay as they were.
  const online = useOnline();
  const overlayManager = useOverlayManager({
    apiClient, team: teamParam, overlays, setOverlays, setVisibleIds, online,
  });
  const [error, setError] = useState<string | null>(null);
  const [storeReady, setStoreReady] = useState(false);
  const [storeState, setStoreState] = useState<StoreSnapshotState>({
    saveState: "idle",
    annotations: null,
  });
  const storeRef = useRef<AnnotationStore | null>(null);
  // Object URL for a cached track.json, set only when the mount effect fell
  // back to IndexedDB. Revoked in that effect's cleanup.
  const trackObjectUrlRef = useRef<string | null>(null);
  const queueRef = useRef<OfflineQueue | null>(null);
  const viewerHandleRef = useRef<ViewerHandle | null>(null);
  const [authExpired, setAuthExpired] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [everSynced, setEverSynced] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [savestateOpen, setSavestateOpen] = useState(false);
  const [savestateBusy, setSavestateBusy] = useState(false);
  const [savestateProgress, setSavestateProgress] =
    useState<SavestateProgress | null>(null);
  const [savestateError, setSavestateError] = useState<string | null>(null);
  const [lastQualityId, setLastQualityId] = useState<string | null>(null);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const [qualities, setQualities] = useState<QualityOption[]>([]);
  const [evictionResult, setEvictionResult] = useState<AuditResult | null>(null);
  // Flight-set siblings (Plan 13): greyed-out tracks on the map, plus the
  // end-of-video "continue to the next flight" prompt.
  const [ghostTracks, setGhostTracks] = useState<GhostTrack[]>(NO_GHOSTS);
  const [endPromptOpen, setEndPromptOpen] = useState(false);
  // Synchronous mirror of pendingCount for the beforeunload handler, which
  // cannot await listMutations().
  const pendingCountRef = useRef(0);
  // undefined = not yet determined; gate the Viewer mount until known so we
  // don't mount in adaptive mode and immediately remount in progressive mode.
  // null = play from the network. resolveCachedFlight owns every precondition
  // for offline playback (matching team, complete download, live :video blob,
  // controlling SW). On an IndexedDB failure (Safari private mode / ITP /
  // locked DB) we also fall back to null. Resolved once per flightId+team — a
  // flight downloaded WHILE the viewer is open takes effect on the next load.
  const [cachedOffline, setCachedOffline] =
    useState<CachedFlightInfo | null | undefined>(undefined);
  const detailTeam = detail?.flight.team;
  useEffect(() => {
    // Reset on every flight/team change so a stale decision from the previous
    // flight can never gate the new one's Viewer mount.
    setCachedOffline(undefined);
    if (flightId === undefined || detailTeam === undefined) return;
    let cancelled = false;
    resolveCachedFlight(flightId, detailTeam)
      .then((info) => { if (!cancelled) setCachedOffline(info); })
      .catch(() => { if (!cancelled) setCachedOffline(null); });
    return () => { cancelled = true; };
  }, [flightId, detailTeam]);

  // Master authority in a foreign scope — a parent opening a sub-client's
  // set — maps onto the viewer's admin gates (edit/delete/drag anything,
  // attribution preserved). The caller's own identity is unchanged, so its
  // edits are attributed to it, not to the sub-client (spec §6.3).
  const ownRole = ownTeam?.role ?? "editor";
  const foreignScope =
    teamParam !== undefined && ownTeam !== null && teamParam !== ownTeam.slug;
  const effectiveRole: "admin" | "editor" | "viewer" =
    foreignScope && ownRole !== "admin" && detail?.access?.master === true ? "admin" : ownRole;
  const reviewer = useMemo(
    () => ({
      reviewerId: ownReviewerId ?? "",
      displayName: displayName ?? ownTeam?.displayName ?? "Anonymous",
      role: effectiveRole,
    }),
    [ownReviewerId, displayName, ownTeam?.displayName, effectiveRole],
  );

  useEffect(() => {
    if (flightId === undefined) {
      setError("missing flight id");
      return;
    }
    let cancelled = false;
    let unsub: (() => void) | null = null;
    // Serialises OfflineQueue count refreshes — see onQueueChanged below.
    let countRefresh: Promise<void> = Promise.resolve();
    (async () => {
      try {
        let flight: FlightDetailResponse;
        let initialAnnotations: Annotations | null;
        let initialEtag: string | null;
        try {
          flight = await apiClient.getFlight(flightId, { team: teamParam });
          const ann = await annotationsApi.get(flightId, teamParam);
          if (cancelled) return;
          initialAnnotations = ann.annotations;
          initialEtag = ann.etag;
        } catch (err) {
          // The Worker is unreachable (offline, or down). Everything this page
          // needs may already be in IndexedDB from a "Download for offline
          // use" — open from there rather than showing a bare fetch error.
          if (cancelled) return;
          const fallback = await buildOfflineDetail(flightId);
          if (cancelled) {
            if (fallback !== null) URL.revokeObjectURL(fallback.trackObjectUrl);
            return;
          }
          if (fallback === null) {
            setError((err as Error).message);
            return;
          }
          trackObjectUrlRef.current = fallback.trackObjectUrl;
          flight = fallback.detail;
          initialAnnotations = fallback.annotations;
          // No ETag offline: the first successful flush is an unconditional
          // PUT, and the OfflineQueue replays a 412 by re-reading the server.
          initialEtag = null;
        }
        const store = new AnnotationStore({
          flightId,
          team: flight.flight.team,
          initial: initialAnnotations,
          initialEtag,
          autoFlush: false, // the OfflineQueue is the sole writer of annotations.json
          api: {
            put: (fId, body, etag) => annotationsApi.put(fId, body, etag, teamParam),
          },
        });
        storeRef.current = store;

        const queue = await OfflineQueue.open({
          flightId,
          reviewerId: reviewer.reviewerId,
          team: flight.flight.team,
          flushAnnotations: async () => {
            const snap = store.snapshot();
            const r = await annotationsApi.put(flightId, snap.annotations, snap.etag, teamParam);
            if (r.kind === "ok") return { kind: "ok", etag: r.etag };
            if (r.kind === "conflict") {
              const fresh = await annotationsApi.get(flightId, teamParam);
              store.applyServerState(fresh.annotations ?? snap.annotations, fresh.etag);
              return { kind: "conflict" };
            }
            if (r.status === 401) throw new AuthExpiredError();
            throw new Error(r.message);
          },
          flushFrame: async (rows) => {
            const full = rows.find((row) => row.variant === "full");
            const thumb = rows.find((row) => row.variant === "thumb");
            if (full === undefined || thumb === undefined) {
              throw new Error("frame rows incomplete");
            }
            const [fullPresign, thumbPresign] = await Promise.all([
              annotationsApi.presignFrame(
                flightId, full.frameId, "full", "image/jpeg", full.blob.size, teamParam,
              ),
              annotationsApi.presignFrame(
                flightId, thumb.frameId, "thumb", "image/jpeg", thumb.blob.size, teamParam,
              ),
            ]);
            await Promise.all([
              annotationsApi.uploadFrame(fullPresign.url, full.blob, "image/jpeg"),
              annotationsApi.uploadFrame(thumbPresign.url, thumb.blob, "image/jpeg"),
            ]);
            // Both variants uploaded — flip hasFull/hasThumb=true on the
            // in-memory frame so the next annotations PUT persists those flags
            // to R2. Without this, FramesPage renders "thumbnail missing"
            // forever even though the blobs landed in R2. The OfflineQueue
            // patches the queued IndexedDB mutation too, but flushAnnotations
            // (above) ignores its `mutations` arg and reads the live store
            // snapshot, so the store IS the source of truth here.
            const existing = store.snapshot().annotations.frames.find(
              (fr) => fr.id === full.frameId,
            );
            if (existing !== undefined && (!existing.hasFull || !existing.hasThumb)) {
              store.upsertFrame({ ...existing, hasFull: true, hasThumb: true });
            }
          },
          onAuthExpired: () => setAuthExpired(true),
          onQueueChanged: () => {
            // Serialise count refreshes so an enqueue's read always lands
            // before the following drain's read — otherwise the 0-crossing
            // that drives everSynced/lastSavedAt can be missed under
            // interleaved IndexedDB reads. The .catch keeps the chain alive
            // after a transient read error.
            countRefresh = countRefresh
              .then(async () => {
                const q = queueRef.current;
                if (q === null) return;
                const rows = await q.listMutations();
                const n = rows.length;
                const prev = pendingCountRef.current;
                if (n === 0 && prev > 0) {
                  setEverSynced(true);
                  setLastSavedAt(Date.now());
                }
                pendingCountRef.current = n;
                setPendingCount(n);
              })
              .catch(() => { /* transient IDB read error — next change re-reads */ });
          },
        });
        queue.start();
        queueRef.current = queue;
        // Drain anything left over from a previous session before the user touches anything.
        void queue.drain();

        unsub = store.subscribe(() => {
          const s = store.snapshot();
          setStoreState({ saveState: s.saveState, annotations: s.annotations });
          // Mirror new in-memory mutations to IndexedDB, then drain so the
          // queue (the single writer) uploads them. pendingCount updates flow
          // back through onQueueChanged.
          const drained = store.drainPendingForPersistence();
          if (drained.length > 0 && queueRef.current !== null) {
            const q = queueRef.current;
            void Promise.all(drained.map((p) => q.enqueueMutation(p.m, p.clientTs)))
              .then(() => { void q.drain(); });
          }
        });
        setDetail(flight);
        const initialSnap = store.snapshot();
        setStoreState({
          saveState: initialSnap.saveState,
          annotations: initialSnap.annotations,
        });
        setStoreReady(true);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (unsub !== null) unsub();
      queueRef.current?.stop();
      void queueRef.current?.close();
      queueRef.current = null;
      if (trackObjectUrlRef.current !== null) {
        URL.revokeObjectURL(trackObjectUrlRef.current);
        trackObjectUrlRef.current = null;
      }
    };
  }, [apiClient, annotationsApi, flightId, teamParam, reviewer.reviewerId]);

  useEffect(() => {
    let cancelled = false;
    setOverlaysReady(false);
    // Offline: /api/overlays is unreachable by design (the service worker
    // never intercepts /api/*) — serve the copies cached with this flight.
    const list =
      !navigator.onLine && flightId !== undefined
        ? loadCachedOverlays(flightId)
        : loadOverlays(apiClient, teamParam).catch((err: unknown) => {
            // navigator.onLine reports a link, not reachability: dead wifi, a
            // captive portal or a Worker 5xx all land here with onLine true.
            // Spec §6.1 wants the cached copies on a failed fetch too, not
            // just on the offline branch (the outer .catch below still
            // degrades to no overlays if the cache is empty or unreadable).
            console.warn("overlay fetch failed; falling back to the cache", err);
            return flightId === undefined ? [] : loadCachedOverlays(flightId);
          });
    list
      .then((loaded) => { if (!cancelled) { setOverlays(loaded); setOverlaysReady(true); } })
      .catch(() => { if (!cancelled) { setOverlays([]); setOverlaysReady(true); } });
    return () => { cancelled = true; };
  }, [apiClient, teamParam, flightId]);

  // Sibling ghost tracks for a flight that belongs to a set. The
  // offline-reconstructed detail blanks every member's trackUrl (presigns are
  // short-lived and useless offline), which is the signal to rebuild the
  // ghosts from the cached `:set-track:*` blobs instead.
  const detailSet = detail?.set;
  useEffect(() => {
    setGhostTracks(NO_GHOSTS);
    setEndPromptOpen(false);
    if (detailSet === undefined || flightId === undefined) return;
    let cancelled = false;
    const load = detailSet.members.some((m) => m.trackUrl !== "")
      ? loadGhostTracks(detailSet, flightId)
      : loadCachedGhostTracks(flightId);
    load
      .then((ghosts) => { if (!cancelled) setGhostTracks(ghosts); })
      .catch((err) => console.warn("ghost track load failed", err));
    return () => { cancelled = true; };
  }, [detailSet, flightId]);

  useEffect(() => {
    if (flightId === undefined) return;
    if (!("serviceWorker" in navigator)) return;
    if (navigator.serviceWorker.controller === null) return;
    navigator.serviceWorker.controller.postMessage({
      type: "vmc:setActiveFlight", flightId,
    });
  }, [flightId]);

  // Offline-only: audit the cached flight assets on mount and surface the
  // EvictionRecoveryModal if any are missing. Online misses fall back to
  // network so no recovery action is needed.
  useEffect(() => {
    if (flightId === undefined) return;
    if (navigator.onLine) return;
    void (async () => {
      const result = await auditFlight(flightId);
      if (result.status !== "ok") {
        setEvictionResult(result);
      }
    })();
  }, [flightId]);

  // Seat presence (spec §5.4): watching a flight is activity, so the heartbeat
  // needs to know when the video is rolling. Leaving the page (or swapping the
  // flight under it) always clears the flag — tearing the element down never
  // fires `pause`, so nothing else would.
  useEffect(() => () => setPlaying(false), [flightId]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): void => {
      // Warn only when there is genuinely unsynced work in the queue.
      if (pendingCountRef.current > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Guided tour (spec 2026-09-04 §4.6-4.7): offer the viewer chapter once the
  // viewer host is on screen, and pause playback when a chapter starts so the
  // auto-hiding controls bar stays visible for its steps.
  useTourChapterHook("viewer", {
    onStart: () => viewerHandleRef.current?.pause(),
    // Hands-on steps read the live annotation snapshot (spec §4.7 rev 2).
    context: { annotations: storeState.annotations, document },
  });
  useTourAutoStart(
    "viewer",
    detail !== null && storeReady && overlaysReady && cachedOffline !== undefined,
  );

  // Basemaps (2026-09-09): keyed Esri tiles for a flight streaming from the
  // network, the NSW Spatial Services layers for one playing from the offline
  // cache — those are what the download stored, and the only ones whose
  // licence allows storing. The Viewer reads this at mount, and the mount is
  // already gated on cachedOffline being decided, so the choice is final for
  // the session (a cached flight keeps its NSW tiles if the link drops).
  // A tile guard that tripped earlier in this session (spec §8.2) means no map
  // on this page may ask Esri for anything, so the choice is made here rather
  // than patched onto the layers after mount — `usesEsri` then drops the
  // counter by itself. The meter is deliberately not a dependency: a trip while
  // this flight is open is handled by the effect below, on the live map.
  const tileLayers = useMemo(
    () => (tileMeter.tripped() ? nswTileLayers() : selectTileLayers({
      online,
      playingFromCache: cachedOffline !== undefined && cachedOffline !== null,
      esriKey: esriApiKey,
    })),
    [online, cachedOffline, esriApiKey],
  );

  // A runaway pan loop is the one thing that can spend real money fast, so past
  // the page's hourly budget the map finishes the session on the free NSW
  // tiles. No dialog — the reviewer keeps working, the map just gets blurrier
  // outside NSW. The meter itself does the warning.
  useEffect(() => tileMeter.onTrip(() => {
    viewerHandleRef.current?.degradeTiles(nswTileLayers());
  }), []);

  if (error !== null) {
    return (
      <p role="alert" style={{ color: "#b00020" }}>
        {error}
      </p>
    );
  }
  if (detail === null || !storeReady || storeRef.current === null || flightId === undefined || !overlaysReady) {
    return <p>Loading viewer…</p>;
  }

  const store = storeRef.current;

  // Full page navigation, matching onOpenFrames/onViewFrame below: the viewer
  // owns a video element, a Leaflet map and an OfflineQueue, so a set hop is a
  // clean teardown rather than an in-place route swap.
  const gotoFlight = (id: string): void => {
    const qs = teamParam !== undefined ? `?team=${encodeURIComponent(teamParam)}` : "";
    window.location.assign(`/flights/${id}${qs}`);
  };
  const setIdx = detail.set?.members.findIndex((m) => m.flightId === flightId) ?? -1;
  const nextMember = detail.set !== undefined && setIdx !== -1
    ? detail.set.members[setIdx + 1]
    : undefined;

  // Scope switcher: the owner's own set plus every sub-client holding the
  // flight. `sharedWith` is only present when the caller masters the
  // owner's scope, so a sub-client never sees this menu (spec §5.4, §6.3).
  const ownerSlug = detail.flight.sharedFrom?.slug ?? detail.flight.team;
  const ownerLabel =
    detail.flight.sharedFrom?.displayName ?? detail.access?.scope.displayName ?? ownerSlug;
  const flightPath = `/flights/${encodeURIComponent(flightId ?? "")}`;
  const ownerHref =
    ownTeam !== null && ownerSlug === ownTeam.slug
      ? flightPath
      : `${flightPath}?team=${encodeURIComponent(ownerSlug)}`;
  const scopeOptions: ScopeOption[] =
    detail.access?.sharedWith !== undefined
      ? [
          { slug: ownerSlug, label: `${ownerLabel} (owner)`, href: ownerHref },
          ...detail.access.sharedWith.map((t) => ({
            slug: t.slug,
            label: t.displayName,
            href: `${flightPath}?team=${encodeURIComponent(t.slug)}`,
          })),
        ]
      : [];

  const videoSource = chooseVideoSource(detail.urls, cachedOffline ?? null, window.location.origin);

  const handleFrameCapture = async (
    frameId: string,
    fullBlob: Blob,
    thumbBlob: Blob,
    meta: {
      videoTimeS: number;
      utc: number;
      lat: number;
      lon: number;
      poiId?: string;
      source?: "video" | "panorama";
      panoramaId?: string;
      panoView?: PanoView;
    },
  ): Promise<void> => {
    const capturedAt = new Date().toISOString();
    // Optimistic in-memory upsert so the gallery shows the frame immediately.
    // The subscribe hook mirrors this as an upsertFrame mutation; enqueueFrame
    // then writes a second (idempotent) upsertFrame row alongside the blobs.
    store.upsertFrame({
      id: frameId,
      schemaVersion: 1,
      videoTimeS: meta.videoTimeS,
      utc: meta.utc,
      lat: meta.lat,
      lon: meta.lon,
      poiId: meta.poiId,
      source: meta.source,
      panoramaId: meta.panoramaId,
      panoView: meta.panoView,
      capturedAt,
      capturedBy: { reviewerId: reviewer.reviewerId, displayName: reviewer.displayName },
      hasThumb: false,
      hasFull: false,
    });
    const q = queueRef.current;
    if (q === null) return;
    await q.enqueueFrame({
      frameId,
      fullBlob,
      thumbBlob,
      clientTs: capturedAt,
      meta,
      capturedBy: { reviewerId: reviewer.reviewerId, displayName: reviewer.displayName },
    });
    void q.drain();
  };

  const handleDownloadKmz = async (): Promise<void> => {
    if (detail === null) return;
    setExporting(true);
    try {
      const panos = await apiClient.getPanoramas(flightId, { team: teamParam });
      const { blob, failures } = await buildKmz({
        meta: detail.flight,
        annotations: store.snapshot().annotations,
        trackUrl: detail.urls.track,
        panoramas: panos?.panoramas ?? [],
        frameUrl: (fId, variant) =>
          annotationsApi.frameUrl(flightId, fId, variant, teamParam),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kmzFilename(detail.flight);
      a.click();
      URL.revokeObjectURL(url);
      if (failures > 0) {
        window.alert(`KMZ exported. ${failures} image(s) could not be included.`);
      }
    } catch (err) {
      window.alert(`KMZ export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenSavestate = async (): Promise<void> => {
    if (detail === null) return;
    const fmt = detail.flight.videoFormat ?? "progressive";
    if (fmt === "progressive") {
      setQualities([{ id: "fmv", label: detail.flight.video.fmv.resolution.label }]);
    } else {
      // HLS: enumerate ladder rungs (largest first) + the preview proxy as the
      // smallest option. Each rung shows its estimated cache size. Exclude
      // rungs above the offline bitrate cap: the Phase-0 seeking spike showed
      // the 80 Mbps "Max" 4K rung cannot be scrubbed when cached offline.
      const options: QualityOption[] = [];
      try {
        if (detail.urls.manifest !== undefined) {
          const master = await fetchManifest(detail.urls.manifest);
          const rungs = parseMasterRungs(master, detail.urls.manifest);
          const dur = detail.flight.video.durationSeconds;
          for (const r of offlineSelectableRungs(rungs)) {
            const gb = (r.bandwidth / 8) * dur / 1_000_000_000;
            const mbps = Math.round(r.bandwidth / 1_000_000);
            options.push({
              id: r.rung,
              label: `${r.resolution} · ${mbps} Mbps · ~${gb.toFixed(1)} GB`,
            });
          }
        }
      } catch (err) {
        console.warn("rung enumeration failed; offering preview only", err);
      }
      options.push({ id: "preview", label: "Preview proxy (smallest)" });
      setQualities(options);
    }
    setSavestateError(null);
    setSavestateOpen(true);
  };

  const describeDownloadError = (err: unknown): string =>
    err instanceof OfflineQuotaExceededError
      ? "Storage full — remove other cached flights on the /offline page"
      : `Offline download failed: ${(err as Error).message}`;

  const handleOfflineDownload = async (qualityId: string): Promise<void> => {
    if (detail === null) return;
    setLastQualityId(qualityId);
    setSavestateError(null);
    setSavestateProgress({ done: 0, total: 1, label: "preparing" });
    setSavestateBusy(true);
    const abort = new AbortController();
    downloadAbortRef.current = abort;
    try {
      // Mirror the live store's annotations into a baseline if present, then
      // delegate URL gathering to the shared helper used by OfflinePage's
      // Refresh action. The helper falls back to fetchAnnotations only when
      // the store is unavailable; here we override fetchAnnotations to return
      // the in-memory snapshot so unflushed edits aren't lost.
      const snap = store.snapshot();
      const liveAnnotations = snap.annotations ?? null;

      const input = await buildDownloadInputFromDetail({
        detail,
        teamParam,
        videoQuality: qualityId,
        fetchPanoramas: (id, team) => apiClient.getPanoramas(id, { team }),
        fetchFramePresign: (id, frameId, variant, team) =>
          annotationsApi.frameUrl(id, frameId, variant, team),
        fetchAnnotations: async () => ({
          annotations: liveAnnotations,
          etag: null,
        }),
        fetchTrackSamples: fetchTrack,
        fetchOverlayList: async (team) => (await apiClient.listOverlays(team)).overlays,
        fetchOverlayBytes: (id, team) => apiClient.getOverlayBytes(id, team),
        authToken: token ?? undefined,
        workerOrigin,
        fetchManifest,
        onProgress: (done, total, label) =>
          setSavestateProgress({ done, total, label }),
      });

      // buildDownloadInputFromDetail predates the AbortSignal, so a Cancel
      // during the "preparing" phase is only observed here — without this
      // guard the operator would wait out the whole download.
      if (abort.signal.aborted) {
        throw new DOMException("offline download aborted", "AbortError");
      }

      await downloadFlightForOffline(input, { signal: abort.signal });

      setSavestateOpen(false);
      setSavestateProgress(null);
      window.dispatchEvent(
        new CustomEvent("vmc:offline-cached", {
          detail: { flightId: detail.flight.flightId },
        }),
      );
    } catch (err) {
      setSavestateProgress(null);
      if ((err as { name?: string }).name === "AbortError") {
        // Operator cancelled: close quietly; cached assets persist for resumption.
        setSavestateOpen(false);
      } else {
        setSavestateError(describeDownloadError(err));
      }
    } finally {
      downloadAbortRef.current = null;
      setSavestateBusy(false);
    }
  };

  const handleCancelDownload = (): void => {
    downloadAbortRef.current?.abort();
  };

  const handleRefresh = async (): Promise<void> => {
    const fresh = await annotationsApi.get(flightId, teamParam);
    store.applyServerState(
      fresh.annotations ?? {
        schemaVersion: 1,
        flightId,
        team: detail.flight.team,
        updatedAt: new Date().toISOString(),
        pois: [],
        pins: [],
        frames: [],
      },
      fresh.etag,
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <header
        style={{
          padding: "8px 12px",
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #2a2a2a",
          background: "#0e0e0e",
          color: "#eee",
          position: "relative",
          zIndex: 1000,
        }}
      >
        <Link
          to="/flights"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#1a1a1a",
            color: "#eee",
            border: "1px solid #333",
            borderRadius: 4,
            textDecoration: "none",
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ← Back to Flight List
        </Link>
        <div
          style={{
            flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <strong style={{ color: "#4cf" }}>{detail.flight.name}</strong>
          {detail.flight.sharedFrom !== undefined
            && detail.flight.sharedFrom.slug !== ownTeam?.slug && (
            <TeamPill
              slug={detail.flight.sharedFrom.slug}
              displayName={`Shared by ${detail.flight.sharedFrom.displayName}`}
            />
          )}
          {foreignScope && teamParam !== undefined && (
            <TeamPill
              slug={teamParam}
              displayName={`Viewing: ${detail.access?.scope.displayName ?? teamParam}`}
            />
          )}
          {detail.set !== undefined && (
            <SetNavChip
              set={detail.set}
              currentFlightId={flightId}
              onNavigate={gotoFlight}
            />
          )}
        </div>
        {storeState.annotations !== null && <EditorList annotations={storeState.annotations} />}
        <SaveStatePill
          state={pillState({ pendingCount, authExpired, everSynced })}
          lastSavedAt={lastSavedAt}
          pendingCount={pendingCount}
        />
        <RefreshButton onRefresh={handleRefresh} />
        {scopeOptions.length > 1 && (
          <ScopeMenu
            options={scopeOptions}
            currentSlug={detail.flight.team}
            onNavigate={(href) => window.location.assign(href)}
          />
        )}
        <OverlayMenu
          overlays={overlays.map((o) => (
            o.inherited === true ? { id: o.id, name: o.name, inherited: true } : { id: o.id, name: o.name }
          ))}
          visibleIds={visibleIds}
          onToggle={(id, on) =>
            setVisibleIds((prev) => {
              const next = new Set(prev);
              if (on) next.add(id); else next.delete(id);
              return next;
            })}
          manager={overlayManager}
        />
        <ExportMenu
          onDownloadKmz={handleDownloadKmz}
          onOfflineSavestate={() => { void handleOpenSavestate(); }}
          busy={exporting}
        />
      </header>
      {/* Gate on the cached-flight decision (three-state, see cachedOffline)
          so the player never mounts adaptive and remounts progressive; the
          video/manifest/preview sources come from chooseVideoSource (virtual
          offline URLs when cached, presigned/adaptive when not), while the
          overlay + footprint props ride the online detail as before. */}
      {cachedOffline !== undefined && (
        <div
          data-testid="viewer-host"
          style={{ flex: 1, minHeight: 0, position: "relative", zIndex: 1 }}
        >
          <Viewer
            videoUrl={videoSource.videoUrl}
            trackUrl={detail.urls.track}
            manifestUrl={videoSource.manifestUrl}
            previewUrl={videoSource.previewUrl}
            authToken={token ?? ""}
            workerOrigin={workerOrigin}
            meta={detail.flight}
            store={store}
            reviewer={reviewer}
            team={teamParam}
            overlays={overlays}
            visibleIds={visibleIds}
            onFrameCapture={handleFrameCapture}
            onOpenFrames={() => {
              const qs = teamParam !== undefined ? `?team=${encodeURIComponent(teamParam)}` : "";
              window.location.assign(`/flights/${flightId}/frames${qs}`);
            }}
            onViewFrame={(frameId) => {
              const qs = teamParam !== undefined
                ? `?team=${encodeURIComponent(teamParam)}&focus=${frameId}`
                : `?focus=${frameId}`;
              window.location.assign(`/flights/${flightId}/frames${qs}`);
            }}
            seekToTime={tParam}
            openPanorama={openPanorama}
            footprintUrl={detail.urls.footprint}
            tileLayers={tileLayers}
            onTileLoad={usesEsri(tileLayers) ? tileMeter.count : undefined}
            ghostTracks={ghostTracks}
            onGhostTrackClick={gotoFlight}
            onVideoEnded={() => setEndPromptOpen(true)}
            onPlayStateChange={setPlaying}
            onHandleReady={(h) => { viewerHandleRef.current = h; }}
          />
          {endPromptOpen && nextMember !== undefined && (
            <NextFlightPrompt
              nextName={nextMember.name}
              onContinue={() => gotoFlight(nextMember.flightId)}
              onDismiss={() => setEndPromptOpen(false)}
            />
          )}
        </div>
      )}
      {savestateOpen && (
        <SavestateDialog
          qualities={qualities}
          progress={savestateProgress}
          busy={savestateBusy}
          error={savestateError}
          onBuild={(q) => { void handleOfflineDownload(q); }}
          onClose={() => { setSavestateOpen(false); setSavestateError(null); }}
          onCancel={handleCancelDownload}
          onRetry={lastQualityId !== null
            ? () => { void handleOfflineDownload(lastQualityId); }
            : undefined}
        />
      )}
      <OverlayDropZone enabled={online} onFiles={overlayManager.addFiles} />
      {evictionResult !== null && (
        <EvictionRecoveryModal
          status={evictionResult.status}
          missing={evictionResult.missing}
          online={navigator.onLine}
          onReconnectDownload={() => {
            setEvictionResult(null);
            void refreshFlight({
              flightId: flightId!,
              teamParam,
              fetchDetail: (id, team) => apiClient.getFlight(id, { team }),
              fetchPanoramas: (id, team) => apiClient.getPanoramas(id, { team }),
              fetchFramePresign: (id, frameId, variant, team) =>
                annotationsApi.frameUrl(id, frameId, variant, team),
              fetchAnnotations: (id, team) => annotationsApi.get(id, team),
              fetchOverlayList: async (team) =>
                (await apiClient.listOverlays(team)).overlays,
              fetchOverlayBytes: (id, team) => apiClient.getOverlayBytes(id, team),
              authToken: token ?? undefined,
              workerOrigin,
              fetchManifest,
            }).catch((err) => {
              setQualities([]);
              setLastQualityId(null);
              setSavestateError(
                describeDownloadError(err).replace("Offline download", "Re-download"),
              );
              setSavestateOpen(true);
            });
          }}
          onClose={() => setEvictionResult(null)}
        />
      )}
    </div>
  );
}
